import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The Service Advisor.
 *
 * Five multiple-choice questions, then a recommendation. The questions come
 * from the server so there is one source of truth — the same file validates
 * the answers on the way back, and an option that does not exist there is
 * refused rather than passed to the model.
 *
 * Everything is keyboard-operable and announced: the whole thing is one live
 * region, because a screen reader user who answers a question and hears
 * nothing has no idea whether it worked.
 */
export default function ServiceAdvisor() {
  const [questions, setQuestions] = useState(null);
  const [available, setAvailable] = useState(false);
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [state, setState] = useState('idle');   // idle | thinking | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/advisor/questions')
      .then((r) => r.json())
      .then((d) => { setQuestions(d.questions || []); setAvailable(Boolean(d.available)); })
      .catch(() => setQuestions([]));
  }, []);

  if (!questions || !available) return null;   // never render a control that cannot work

  const question = questions[step];
  const last = step === questions.length - 1;

  async function submit(finalAnswers) {
    setState('thinking');
    setError(null);
    try {
      const res = await fetch('/api/advisor', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ answers: finalAnswers }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Could not produce a recommendation.');
      setResult(body);
      setState('done');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  }

  function choose(value) {
    const next = { ...answers, [question.id]: value };
    setAnswers(next);
    if (last) submit(next);
    else setStep((s) => s + 1);
  }

  function toggle(value) {
    const current = answers[question.id] || [];
    setAnswers({
      ...answers,
      [question.id]: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  }

  function advance() {
    // A multi-select with nothing ticked is a legitimate answer, and the server
    // accepts it — "none of these" is information too.
    const next = { ...answers, [question.id]: answers[question.id] || [] };
    setAnswers(next);
    if (last) submit(next);
    else setStep((s) => s + 1);
  }

  function reset() {
    setStarted(false); setStep(0); setAnswers({});
    setResult(null); setError(null); setState('idle');
  }

  /* ── Intro ────────────────────────────────────────────────── */
  if (!started) {
    return (
      <div className="advisor advisor-intro">
        <p className="eyebrow">Service advisor</p>
        <h2>Find the right starting point.</h2>
        <p className="lede" style={{ marginTop: '1rem' }}>
          Five questions about your organisation, and you get a recommendation
          on where to begin — written for your situation rather than assembled
          from a template.
        </p>
        <ul className="advisor-facts">
          {['5 questions', 'About 2 minutes', 'No signup', 'Nothing stored'].map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
        <button type="button" className="btn btn-primary" onClick={() => setStarted(true)}>
          Start
        </button>
      </div>
    );
  }

  /* ── Thinking ─────────────────────────────────────────────── */
  if (state === 'thinking') {
    return (
      <div className="advisor advisor-waiting" aria-live="polite">
        <p className="eyebrow">Working</p>
        <h3>Reading your answers.</h3>
        <p className="dim" style={{ marginTop: '0.6rem' }}>
          A few seconds. It is writing something specific, not looking one up.
        </p>
        <div className="advisor-bar"><span /></div>
      </div>
    );
  }

  /* ── Result ───────────────────────────────────────────────── */
  if (state === 'done' && result) {
    return (
      <div className="advisor advisor-result" aria-live="polite">
        <p className="eyebrow">Your recommendation</p>
        <h2>{result.headline}</h2>
        <p className="lede" style={{ marginTop: '1rem', maxWidth: '46rem' }}>{result.summary}</p>

        <div className="advisor-primary railed">
          <p className="eyebrow eyebrow-grey" style={{ margin: 0 }}>Start here</p>
          <h3 style={{ marginTop: '0.5rem' }}>{result.primary_service}</h3>
          <p className="dim" style={{ marginTop: '0.6rem' }}>{result.primary_reason}</p>
        </div>

        <div className="grid grid-2" style={{ marginTop: '2rem' }}>
          <div>
            <h4 className="advisor-head">First steps</h4>
            <ol className="advisor-wins">
              {result.quick_wins.map((w) => <li key={w}>{w}</li>)}
            </ol>
          </div>
          <div>
            {result.secondary_services.length > 0 && (
              <>
                <h4 className="advisor-head">Also relevant</h4>
                <ul className="chip-list" style={{ marginTop: '0.7rem' }}>
                  {result.secondary_services.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </>
            )}
            <h4 className="advisor-head" style={{ marginTop: '1.6rem' }}>On timing</h4>
            <p className="dim" style={{ fontSize: '0.94rem' }}>{result.urgency_note}</p>
          </div>
        </div>

        <div className="hero-actions" style={{ marginTop: '2.2rem' }}>
          <Link to="/contact?kind=consultation" className="btn btn-primary">Talk this through</Link>
          <button type="button" className="btn btn-ghost" onClick={reset}>Start again</button>
        </div>

        <p className="faint" style={{ marginTop: '1.4rem', fontSize: '0.82rem', maxWidth: '42rem' }}>
          This is a starting point from five answers, not an assessment — nobody
          has seen your environment yet. Your answers were not stored.
        </p>
      </div>
    );
  }

  /* ── Questions ────────────────────────────────────────────── */
  const selected = answers[question.id];

  return (
    <div className="advisor">
      <div className="advisor-progress">
        <span className="mono">{step + 1} / {questions.length}</span>
        <div className="advisor-bar" role="presentation">
          <span style={{ width: `${((step + 1) / questions.length) * 100}%` }} />
        </div>
      </div>

      <fieldset className="advisor-fieldset">
        <legend>
          <h3>{question.question}</h3>
          {question.subtitle && <p className="faint" style={{ margin: '0.4rem 0 0', fontSize: '0.85rem' }}>{question.subtitle}</p>}
        </legend>

        <div className="advisor-options">
          {question.options.map((o) => {
            const on = question.type === 'multi'
              ? (selected || []).includes(o.value)
              : selected === o.value;
            return (
              <button
                key={o.value}
                type="button"
                className={`advisor-option${on ? ' on' : ''}`}
                aria-pressed={question.type === 'multi' ? on : undefined}
                onClick={() => (question.type === 'multi' ? toggle(o.value) : choose(o.value))}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      {error && <p className="check-error" role="alert">{error}</p>}

      <div className="advisor-nav">
        {step > 0 && (
          <button type="button" className="btn btn-ghost" onClick={() => setStep((s) => s - 1)}>
            Back
          </button>
        )}
        {question.type === 'multi' && (
          <button type="button" className="btn btn-primary" onClick={advance}>
            {last ? 'Get my recommendation' : 'Continue'}
          </button>
        )}
      </div>
    </div>
  );
}
